import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getSurveysFilterParams, queryToSurveysFilters } from '../lib/surveys-query'
import { useSurveysStore } from '../model/store'

interface SurveysExportOptions {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `POST /admin-api/surveys/export` — queues the CSV, which is emailed to the admin. Sends the filters
 * applied in the URL rather than the store, so a search still inside its debounce window is not exported.
 */
export const useSurveysExport = async (options?: SurveysExportOptions): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useSurveysStore()

  const url = useAdminRoutes('admin.surveys.export')

  store.setExporting(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return false
    }

    const { data } = await $axios.post<ApiResponse>(url, {
      ...getSurveysFilterParams(queryToSurveysFilters(route.query)),
      discipline: store.getDisciplineId ?? undefined,
    })

    if (!data.success) {
      toast.error(data.message)
      return false
    }

    toast.success(data.message)

    return true
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return false
  } finally {
    store.setExporting(false)
  }
}
